'use client'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { countries } from '@/modules/hs/constants/country.constant'
import { ChevronDown, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'

interface CountrySelectorProps {
    value: string
    onValueChange: (value: string) => void
    placeholder?: string
    className?: string
    error?: boolean
}

// Separate popular and other countries
const popularCountries = countries.filter(c => c.popular)
const otherCountries = countries.filter(c => !c.popular)

// Custom filter: match against combined "label vietnameseName" string (already set as value prop on CommandItem)
const filterCountry = (itemValue: string, search: string): number => {
    return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
}

export default function CountrySelector({
    value,
    onValueChange,
    placeholder = 'Chọn quốc gia...',
    className,
    error = false,
}: CountrySelectorProps) {
    const [open, setOpen] = useState(false)

    const selectCountry = (countryCode: string) => {
        onValueChange(countryCode)
        setOpen(false)
    }

    const selectedCountry = countries.find(c => c.value === value)
    const displayValue = selectedCountry
        ? selectedCountry.vietnameseName || selectedCountry.label
        : placeholder

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        'w-full justify-between font-normal h-10',
                        error && 'border-red-500',
                        className
                    )}
                >
                    {displayValue}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
                <Command filter={filterCountry}>
                    <CommandInput placeholder="Tìm kiếm quốc gia..." />
                    <CommandList>
                        <CommandEmpty>Không tìm thấy quốc gia</CommandEmpty>
                        <CommandGroup heading="Phổ biến">
                            {popularCountries.map((c) => (
                                <CommandItem
                                    key={c.value}
                                    value={`${c.label} ${c.vietnameseName} ${c.value}`}
                                    onSelect={() => selectCountry(c.value)}
                                    className="cursor-pointer"
                                >
                                    <Check
                                        className={cn(
                                            'mr-2 h-4 w-4 shrink-0',
                                            value === c.value ? 'opacity-100' : 'opacity-0'
                                        )}
                                    />
                                    <span className="flex-1 truncate">{c.label}</span>
                                    {c.vietnameseName && (
                                        <span className="ml-2 text-xs text-muted-foreground truncate">
                                            {c.vietnameseName}
                                        </span>
                                    )}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        <CommandGroup heading="Tất cả quốc gia">
                            {otherCountries.map((c) => (
                                <CommandItem
                                    key={c.value}
                                    value={`${c.label} ${c.vietnameseName} ${c.value}`}
                                    onSelect={() => selectCountry(c.value)}
                                    className="cursor-pointer"
                                >
                                    <Check
                                        className={cn(
                                            'mr-2 h-4 w-4 shrink-0',
                                            value === c.value ? 'opacity-100' : 'opacity-0'
                                        )}
                                    />
                                    <span className="flex-1 truncate">{c.label}</span>
                                    {c.vietnameseName && (
                                        <span className="ml-2 text-xs text-muted-foreground truncate">
                                            {c.vietnameseName}
                                        </span>
                                    )}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
